import { computed, Injectable, signal } from '@angular/core';
import { VisitApprovalProgressEvent, VisitApprovalProgressSnapshot, VisitApprovalRowProgress } from '@jms/model';

/**
 * Live per-row approval progress, keyed by `occurrenceId`.
 *
 * ONE instance per board. The board owns the single room join and feeds this; renderers only read it,
 * so a table of 200 rows holds 200 readers of one map rather than 200 sockets.
 *
 * Provided at the board component, never in root: the map is scoped to one review period and must die
 * with it, or a second board would inherit the first board's rows.
 */
@Injectable()
export class VisitApprovalProgressStore {
  private readonly rows = signal<ReadonlyMap<string, VisitApprovalRowProgress>>(new Map());
  private readonly operations = signal<ReadonlySet<string>>(new Set());

  /** True while any operation this store knows about is still running. */
  readonly active = computed(() => this.operations().size > 0);
  readonly size = computed(() => this.rows().size);

  progressFor(occurrenceId: string): VisitApprovalRowProgress | undefined {
    return this.rows().get(occurrenceId);
  }

  /** The signal renderers depend on; reading the map itself keeps one subscription per renderer. */
  readonly snapshot = this.rows.asReadonly();

  /**
   * Replaces what a join-time read covers, rather than merging into it.
   *
   * The snapshot IS database truth for the operations it names, so a delta that arrived before it and
   * is older must not survive underneath. Rows for operations the snapshot does not name are kept:
   * they belong to an operation that finished between the delta and the read, and `complete` is what
   * removes those.
   */
  applySnapshot(snapshot: VisitApprovalProgressSnapshot): void {
    const active = new Set(snapshot.activeOperationIds);
    const next = new Map<string, VisitApprovalRowProgress>();
    for (const [occurrenceId, row] of this.rows()) {
      if (!active.has(row.operationId)) next.set(occurrenceId, row);
    }
    for (const row of snapshot.rows) next.set(row.occurrenceId, row);
    this.rows.set(next);
    this.operations.set(active);
  }

  /**
   * Applies one delta, dropping anything a newer sequence already superseded.
   *
   * `sequence` is the operation's event revision at the moment the state was decided, so a retried
   * Queued delivered after its own Approved carries the lower number and loses. Equal sequences are
   * also dropped: the same revision cannot legitimately describe a row twice.
   */
  applyProgress(event: VisitApprovalProgressEvent): void {
    const incoming = event.payload.progress;
    const known = this.rows().get(incoming.occurrenceId);
    if (known && known.operationId === incoming.operationId && known.sequence >= incoming.sequence) return;
    const next = new Map(this.rows());
    next.set(incoming.occurrenceId, incoming);
    this.rows.set(next);
    if (!this.operations().has(incoming.operationId)) this.operations.set(new Set([...this.operations(), incoming.operationId]));
  }

  /**
   * Drops everything one finished operation contributed.
   *
   * Called when the operation reaches a terminal state and the board reloads: from that moment the
   * fetched `approvalStatus` is the truth, and a stale live badge would contradict it.
   */
  complete(operationId: string): void {
    const next = new Map<string, VisitApprovalRowProgress>();
    for (const [occurrenceId, row] of this.rows()) {
      if (row.operationId !== operationId) next.set(occurrenceId, row);
    }
    this.rows.set(next);
    const operations = new Set(this.operations());
    operations.delete(operationId);
    this.operations.set(operations);
  }

  clear(): void {
    this.rows.set(new Map());
    this.operations.set(new Set());
  }
}
